import { resolveParticipantId } from './participantIds'
import { createSessionParticipant } from './sessionParticipant'

function isPlainObject(value) {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function normalizeOptionalString(value, fallbackValue = null) {
  if (typeof value !== 'string') {
    return fallbackValue
  }

  const normalizedValue = value.trim()

  return normalizedValue || fallbackValue
}

function normalizeBoolean(value, fallbackValue) {
  return typeof value === 'boolean' ? value : fallbackValue
}

function hasOwn(objectValue, propertyName) {
  return Object.prototype.hasOwnProperty.call(objectValue, propertyName)
}

function pickDefined(...values) {
  for (const value of values) {
    if (value !== undefined) {
      return value
    }
  }

  return undefined
}

function pickOptionalString(...values) {
  for (const value of values) {
    const normalizedValue = normalizeOptionalString(value)

    if (normalizedValue !== null) {
      return normalizedValue
    }
  }

  return null
}

function createEmptyTransportBindings() {
  return {}
}

function cloneRosterState(state) {
  return {
    participantsById: { ...(state?.participantsById || {}) },
    participantOrder: [...(state?.participantOrder || [])],
    transportBindings: { ...(state?.transportBindings || {}) },
  }
}

const TRANSPORT_BINDING_FIELDS = Object.freeze([
  'transportParticipantId',
  'sessionMemberId',
  'peerId',
  'participantSid',
  'uid',
  'rtcUid',
  'connectionId',
])

/**
 * @typedef {object} ParticipantRosterState
 * @property {Record<string, import('./sessionParticipant').SessionParticipant>} participantsById
 * @property {string[]} participantOrder
 * @property {Record<string, string>} transportBindings
 */

export const PARTICIPANT_ROSTER_ACTION_TYPES = Object.freeze({
  reset: 'reset',
  rosterReplaced: 'roster-replaced',
  participantUpserted: 'participant-upserted',
  participantMediaChanged: 'participant-media-changed',
  participantLeft: 'participant-left',
})

/**
 * @param {object} [transport={}]
 * @returns {string[]}
 */
export function resolveTransportBindingKeys(transport = {}) {
  if (!isPlainObject(transport)) {
    return []
  }

  const providerId =
    normalizeOptionalString(
      transport.providerId || transport.provider || transport.rtcProvider,
    ) || 'unknown'
  const bindingKeys = []

  TRANSPORT_BINDING_FIELDS.forEach((fieldName) => {
    const fieldValue = normalizeOptionalString(transport[fieldName])

    if (fieldValue) {
      bindingKeys.push(`${providerId}:${fieldName}:${fieldValue}`)
    }
  })

  return [...new Set(bindingKeys)]
}

function mergeParticipant(existingParticipant, incomingParticipant) {
  const existingTransport = isPlainObject(existingParticipant?.transport)
    ? existingParticipant.transport
    : {}
  const incomingTransport = isPlainObject(incomingParticipant?.transport)
    ? incomingParticipant.transport
    : {}
  const mergedTransport = {
    ...existingTransport,
    ...incomingTransport,
  }
  const nextParticipant = incomingParticipant || {}
  const hasVideoHandle = hasOwn(nextParticipant, 'videoHandle')

  return createSessionParticipant({
    ...existingParticipant,
    ...nextParticipant,
    participantId: resolveParticipantId(nextParticipant) || existingParticipant?.id,
    userId: pickOptionalString(nextParticipant?.userId, existingParticipant?.userId),
    actorType: pickOptionalString(
      nextParticipant?.actorType,
      existingParticipant?.actorType,
    ),
    actorId: pickOptionalString(
      nextParticipant?.actorId ?? nextParticipant?.stableActorId,
      existingParticipant?.actorId,
    ),
    displayName: pickOptionalString(
      nextParticipant?.displayName,
      existingParticipant?.displayName,
      '',
    ),
    profilePicId: pickOptionalString(
      nextParticipant?.profilePicId ?? nextParticipant?.avatarImageId,
      existingParticipant?.profilePicId,
    ),
    role: pickOptionalString(nextParticipant?.role, existingParticipant?.role),
    isLocal: normalizeBoolean(
      nextParticipant?.isLocal,
      normalizeBoolean(existingParticipant?.isLocal, false),
    ),
    connectionState: pickDefined(
      nextParticipant?.connectionState,
      existingParticipant?.connectionState,
      null,
    ),
    audioMuted: normalizeBoolean(
      nextParticipant?.audioMuted,
      normalizeBoolean(existingParticipant?.audioMuted, false),
    ),
    videoMuted: normalizeBoolean(
      nextParticipant?.videoMuted,
      normalizeBoolean(existingParticipant?.videoMuted, false),
    ),
    screenSharing: normalizeBoolean(
      nextParticipant?.screenSharing,
      normalizeBoolean(existingParticipant?.screenSharing, false),
    ),
    videoHandle: hasVideoHandle
      ? nextParticipant.videoHandle
      : existingParticipant?.videoHandle ?? null,
    renderKey: pickOptionalString(
      nextParticipant?.renderKey,
      existingParticipant?.renderKey,
      resolveParticipantId(nextParticipant),
      existingParticipant?.id,
    ),
    transport: mergedTransport,
  })
}

function clearParticipantBindings(nextState, participantId) {
  Object.entries(nextState.transportBindings).forEach(
    ([bindingKey, boundParticipantId]) => {
      if (boundParticipantId === participantId) {
        delete nextState.transportBindings[bindingKey]
      }
    },
  )
}

function upsertParticipant(nextState, participant) {
  const participantId = resolveParticipantId(participant)

  if (!participantId) {
    throw new Error(
      '[live/domain] Participant roster updates require a canonical participant id.',
    )
  }

  const mergedParticipant = mergeParticipant(
    nextState.participantsById[participantId],
    participant,
  )

  nextState.participantsById[participantId] = mergedParticipant

  if (!nextState.participantOrder.includes(participantId)) {
    nextState.participantOrder.push(participantId)
  }

  clearParticipantBindings(nextState, participantId)

  resolveTransportBindingKeys(mergedParticipant.transport).forEach((bindingKey) => {
    nextState.transportBindings[bindingKey] = participantId
  })

  return nextState
}

function resolveParticipantIdFromAction(state, action = {}) {
  const participantId = resolveParticipantId(action?.participant || action)

  if (participantId) {
    return participantId
  }

  const bindingKeys = action?.transportBindingKey
    ? [action.transportBindingKey]
    : resolveTransportBindingKeys(action?.transport)

  for (const bindingKey of bindingKeys) {
    if (state.transportBindings[bindingKey]) {
      return state.transportBindings[bindingKey]
    }
  }

  return null
}

/**
 * @param {Array<object>} [participants=[]]
 * @returns {ParticipantRosterState}
 */
export function createParticipantRosterState(participants = []) {
  let nextState = {
    participantsById: {},
    participantOrder: [],
    transportBindings: createEmptyTransportBindings(),
  }

  participants.forEach((participant) => {
    nextState = upsertParticipant(nextState, participant)
  })

  return nextState
}

/**
 * @param {ParticipantRosterState | null | undefined} state
 * @returns {import('./sessionParticipant').SessionParticipant[]}
 */
export function getParticipantRosterParticipants(state) {
  const participantOrder = state?.participantOrder || []
  const participantsById = state?.participantsById || {}

  return participantOrder
    .map((participantId) => participantsById[participantId])
    .filter(Boolean)
}

/**
 * @param {ParticipantRosterState | null | undefined} state
 * @param {string | null | undefined} participantId
 * @returns {import('./sessionParticipant').SessionParticipant | null}
 */
export function getParticipantRosterParticipant(state, participantId) {
  const resolvedParticipantId = normalizeOptionalString(participantId)

  if (!resolvedParticipantId) {
    return null
  }

  return state?.participantsById?.[resolvedParticipantId] || null
}

/**
 * @param {ParticipantRosterState | null | undefined} state
 * @param {string | null | undefined} transportBindingKey
 * @returns {string | null}
 */
export function findParticipantIdByTransportBinding(state, transportBindingKey) {
  const normalizedBindingKey = normalizeOptionalString(transportBindingKey)

  if (!normalizedBindingKey) {
    return null
  }

  return state?.transportBindings?.[normalizedBindingKey] || null
}

/**
 * @param {ParticipantRosterState | null | undefined} state
 * @param {object} [action={}]
 * @returns {ParticipantRosterState}
 */
export function reduceParticipantRoster(state, action = {}) {
  const currentState =
    state && isPlainObject(state)
      ? cloneRosterState(state)
      : createParticipantRosterState()
  const actionType = action?.type

  switch (actionType) {
    case PARTICIPANT_ROSTER_ACTION_TYPES.reset:
      return createParticipantRosterState()
    case PARTICIPANT_ROSTER_ACTION_TYPES.rosterReplaced:
      return createParticipantRosterState(action?.participants || [])
    case PARTICIPANT_ROSTER_ACTION_TYPES.participantUpserted:
      return upsertParticipant(currentState, action?.participant || action)
    case PARTICIPANT_ROSTER_ACTION_TYPES.participantMediaChanged: {
      const participantId = resolveParticipantIdFromAction(currentState, action)
      const existingParticipant = participantId
        ? currentState.participantsById[participantId]
        : null

      if (!existingParticipant && !action?.allowCreate) {
        return currentState
      }

      return upsertParticipant(currentState, {
        ...existingParticipant,
        ...action?.participant,
        participantId: participantId || resolveParticipantId(action?.participant),
        connectionState: pickDefined(
          action?.participant?.connectionState,
          action?.connectionState,
          existingParticipant?.connectionState,
        ),
        audioMuted: pickDefined(
          action?.participant?.audioMuted,
          action?.audioMuted,
          existingParticipant?.audioMuted,
        ),
        videoMuted: pickDefined(
          action?.participant?.videoMuted,
          action?.videoMuted,
          existingParticipant?.videoMuted,
        ),
        screenSharing: pickDefined(
          action?.participant?.screenSharing,
          action?.screenSharing,
          existingParticipant?.screenSharing,
        ),
        videoHandle: hasOwn(action, 'videoHandle')
          ? action.videoHandle
          : pickDefined(
              action?.participant?.videoHandle,
              existingParticipant?.videoHandle,
              null,
            ),
        renderKey: pickOptionalString(
          action?.participant?.renderKey,
          action?.renderKey,
          existingParticipant?.renderKey,
          participantId,
        ),
        transport: {
          ...(existingParticipant?.transport || {}),
          ...(action?.transport || {}),
          ...(action?.participant?.transport || {}),
        },
      })
    }
    case PARTICIPANT_ROSTER_ACTION_TYPES.participantLeft: {
      const participantId = resolveParticipantIdFromAction(currentState, action)

      if (!participantId || !currentState.participantsById[participantId]) {
        return currentState
      }

      delete currentState.participantsById[participantId]
      currentState.participantOrder = currentState.participantOrder.filter(
        (candidateId) => candidateId !== participantId,
      )
      clearParticipantBindings(currentState, participantId)
      return currentState
    }
    default:
      return currentState
  }
}
